import { useEffect, useRef, useCallback } from "react";

const IDLE_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

interface UseIdleTimeoutOptions {
  idleMinutes: number;
  warningMinutes: number;
  onIdle: () => void;
  onWarning: () => void;
  onActivity?: () => void;
}

export function useIdleTimeout({
  idleMinutes,
  warningMinutes,
  onIdle,
  onWarning,
  onActivity,
}: UseIdleTimeoutOptions) {
  const idleTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isWarned     = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimer.current)  clearTimeout(idleTimer.current);
    if (warnTimer.current)  clearTimeout(warnTimer.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    isWarned.current = false;

    const idleMs   = idleMinutes    * 60 * 1000;
    const warnMs   = warningMinutes * 60 * 1000;

    // Warning fires before idle timeout
    warnTimer.current = setTimeout(() => {
      if (!isWarned.current) {
        isWarned.current = true;
        onWarning();
      }
    }, idleMs - warnMs);

    // Idle fires after full idle period
    idleTimer.current = setTimeout(() => {
      onIdle();
    }, idleMs);
  }, [idleMinutes, warningMinutes, onIdle, onWarning, clearTimers]);

  const handleActivity = useCallback(() => {
    resetTimers();
    onActivity?.();
  }, [resetTimers, onActivity]);

  useEffect(() => {
    resetTimers();

    IDLE_EVENTS.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      clearTimers();
      IDLE_EVENTS.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [resetTimers, handleActivity, clearTimers]);
}
